import type { NamedFieldProps } from "./Field.js";
import type { ImmutableMap, ValueCallback } from "shelving";
import { AllowStringSchema, OptionalSchema, getSource, getString, mapArray } from "shelving";
import { Field, useField } from "./Field.js";
import { FieldNotice } from "./FieldNotice.js";

/** Select field: show a `<select>` box */
export function SelectField(props: NamedFieldProps): React.ReactElement {
	const field = useField<string>(props);
	const { name, form, schema, value } = field;
	const { allow } = getSource(AllowStringSchema, schema);
	return (
		<Field {...field}>
			<SelectInput //
				name={name}
				value={value}
				mods="space-none"
				allow={allow}
				onValue={nextValue => form.publish(name, nextValue)}
				required={!(schema instanceof OptionalSchema)}
			/>
			<FieldNotice name={name} />
		</Field>
	);
}

export function SelectInput<T extends string>({
	mods = "",
	placeholder = "None",
	value,
	allow,
	onValue,
	required = false,
	...props
}: {
	name?: string | undefined;
	mods?: string | undefined;
	placeholder?: string | undefined;
	allow: ImmutableMap<T, unknown>;
	required?: boolean | undefined;
	value?: T | undefined;
	onValue: ValueCallback<T>;
}): React.ReactElement {
	return (
		<select //
			{...props}
			required={required}
			className={`input ${mods}`}
			defaultValue={value}
			onChange={({ currentTarget }) => onValue(currentTarget.value as T)}
		>
			{!required ? <option value="">{placeholder}</option> : null}
			{mapArray(allow, ([k, t]) => (
				<option key={k} value={k}>
					{getString(t)}
				</option>
			))}
		</select>
	);
}
